import CheckCircleIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmberRounded";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import HelpArticleLink from "@entrilia/shared/components/HelpArticleLink";
import DetailedTooltipTitle from "../../../../../common/DetailedTooltipTitle";
import type { PortalAccessRowStatus } from "./portalAccessModel";

interface Props {
  label: string;
  value?: ReactNode;
  caption?: string | string[];
  help?: string;
  helpDetails?: string;
  // Renders as "Learn more" after the help text, so a row with no `help` shows no icon and no article.
  helpArticleId?: string;
  actions?: ReactNode;
  status?: PortalAccessRowStatus;
  captionStatus?: Extract<PortalAccessRowStatus, "problem">;
}

const PortalAccessRow = ({
  label,
  value,
  caption,
  help,
  helpDetails,
  helpArticleId,
  actions,
  status,
  captionStatus,
  children,
}: PropsWithChildren<Props>) => {
  const captions = typeof caption === "string" ? [caption] : (caption ?? []);

  return (
    <Box
      sx={{ px: 2.5, py: 1.5, display: "flex", flexWrap: "wrap", alignItems: "flex-start", columnGap: 2, rowGap: 1 }}
    >
      <Typography
        component="dt"
        variant="subtitle2"
        color="textSecondary"
        sx={{ flex: "0 1 8rem", display: "flex", alignItems: "center", minHeight: "2rem" }}
      >
        {label}
      </Typography>
      <Stack component="dd" sx={{ flex: "1 1 10rem", minWidth: 0, m: 0 }}>
        {value && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", minWidth: 0, minHeight: "2rem" }}>
            {status === "problem" && <WarningAmberIcon color="warning" />}
            {status === "completed" && <CheckCircleIcon color="success" />}
            {typeof value === "string" ? <Typography component="span">{value}</Typography> : value}
            {help && (
              <Tooltip
                arrow
                describeChild
                slotProps={{ tooltip: { sx: { maxHeight: "70vh", overflowY: "auto" } } }}
                title={
                  <>
                    {helpDetails ? <DetailedTooltipTitle title={help} details={helpDetails} /> : help}
                    {helpArticleId && (
                      <>
                        {" "}
                        <HelpArticleLink articleId={helpArticleId} sx={{ font: "inherit" }}>
                          Learn more
                        </HelpArticleLink>
                      </>
                    )}
                  </>
                }
              >
                <InfoOutlinedIcon sx={{ color: "text.secondary" }} />
              </Tooltip>
            )}
          </Stack>
        )}
        {children}
        {captions.map((text, index) => (
          <Typography key={text} variant="caption" color="textSecondary">
            {index === 0 && captionStatus === "problem" && (
              <WarningAmberIcon color="warning" fontSize="inherit" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            )}
            {text}
          </Typography>
        ))}
      </Stack>
      {actions && (
        <Stack spacing={1} sx={{ flex: "0 1 auto", ml: "auto", alignItems: "flex-end", minHeight: "2rem" }}>
          {actions}
        </Stack>
      )}
    </Box>
  );
};

export default PortalAccessRow;
